import { useEffect, useState } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { authApi, setCsrfToken, ApiError, type LoginResponse } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/hooks/useAuth"
import { TotpChallenge } from "./TotpChallenge"
import { TotpEnrolFlow } from "./TotpEnrolFlow"

type Stage =
  | { kind: "credentials" }
  | { kind: "totp"; challengeId: string; email: string }
  | { kind: "enrol"; enrolId: string; secret: string; uri: string; email: string }

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoading } = useAuth()
  const qc = useQueryClient()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [stage, setStage] = useState<Stage>({ kind: "credentials" })
  const [error, setError] = useState<string | null>(null)

  // If already logged in, redirect
  useEffect(() => {
    if (user) {
      const from = (location.state as { from?: string } | null)?.from ?? "/"
      navigate(from, { replace: true })
    }
  }, [user, navigate, location])

  const loginMutation = useMutation({
    mutationFn: (vars: { email: string; password: string }) =>
      authApi.login(vars.email, vars.password),
    onSuccess: (data: LoginResponse) => {
      setError(null)
      if ("requiresEnrol" in data) {
        setStage({
          kind: "enrol",
          enrolId: data.enrolId,
          secret: data.totpSecret,
          uri: data.totpUri,
          email,
        })
      } else {
        setStage({ kind: "totp", challengeId: data.challengeId, email })
      }
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError("Something went wrong. Please try again.")
      }
    },
  })

  const handleLoginSuccess = async (csrf: string) => {
    setCsrfToken(csrf)
    await qc.invalidateQueries({ queryKey: ["me"] })
    const from = (location.state as { from?: string } | null)?.from ?? "/"
    navigate(from, { replace: true })
  }

  if (isLoading) {
    return (
      <div className="rc-login-shell grid min-h-screen place-items-center text-white/70">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  return (
    <div className="rc-login-shell grid min-h-screen place-items-center p-4">
      <div className="rc-login-grid" aria-hidden />

      <div className="relative z-10 w-full max-w-md">
        {/* Brand */}
        <div className="rc-fade-up mb-10 flex flex-col items-center text-center">
          <span className="rc-login-logo-glow mb-6">
            <img
              src="/brand/rivercity-logo-dark.png"
              srcSet="/brand/rivercity-logo-dark.png 1x, /brand/rivercity-logo-dark@2x.png 2x"
              alt="Rivercity Finance"
              className="h-28 w-auto sm:h-32"
            />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
            Welcome back
          </h1>
          <p className="mt-2 text-sm text-white/60">
            Sign in to your Rivercity Finance dashboard
          </p>
        </div>

        {stage.kind === "credentials" && (
          <div className="rc-glass-card rc-fade-up-delay-1 rounded-2xl p-8">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                setError(null)
                loginMutation.mutate({ email, password })
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-white/50 transition-colors hover:text-white"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              {error ? (
                <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                className="h-11 w-full bg-gradient-to-r from-rc-blue-600 to-cyan-500 text-base font-medium text-white shadow-lg shadow-blue-900/40 transition-all hover:from-rc-blue-500 hover:to-cyan-400 hover:shadow-blue-900/60"
                disabled={loginMutation.isPending}
              >
                {loginMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Sign in"
                )}
              </Button>

              <p className="pt-2 text-center text-xs text-white/40">
                🔐 Two-factor authentication required on every sign-in
              </p>
            </form>
          </div>
        )}

        {stage.kind === "totp" && (
          <div className="rc-glass-card rc-fade-up-delay-1 rounded-2xl p-8">
            <TotpChallenge
              challengeId={stage.challengeId}
              email={stage.email}
              onSuccess={handleLoginSuccess}
              onBack={() => {
                setStage({ kind: "credentials" })
                setPassword("")
              }}
            />
          </div>
        )}

        {stage.kind === "enrol" && (
          <div className="rc-glass-card rc-fade-up-delay-1 rounded-2xl p-8">
            <TotpEnrolFlow
              enrolId={stage.enrolId}
              secret={stage.secret}
              uri={stage.uri}
              email={stage.email}
              onSuccess={handleLoginSuccess}
            />
          </div>
        )}

        <p className="rc-fade-up-delay-2 mt-8 text-center text-xs text-white/40">
          © {new Date().getFullYear()} Rivercity Finance — Asset finance, simplified.
        </p>
      </div>
    </div>
  )
}
